import type React from 'react';
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getRouteApi } from '@tanstack/react-router';
import { SearchIcon, XIcon } from 'lucide-react';

import { Input as NewUiInput } from '@/components/new-ui/input';
import type { SelectOption } from '@/components/ui/select';
import { useDebouncedCallback } from '@/hooks/use-debounced-callback';
import { FilterButton } from '@/features/estates/components/filter-button';
import { EstatesFilterTrigger } from '@/features/estates/components/estates-filter-trigger';
import { EstatesNewEstateButton } from '@/features/estates/components/estates-new-estate-button';
import { EstatesQuickFilters } from '@/features/estates/components/estates-quick-filters';
import { EstatesViewMenu } from '@/features/estates/components/estates-view-menu';
import { ViewSwitch } from '@/features/estates/components/view-switch';
import { cn } from '@/lib/utils';

import { ESTATES_TOOLBAR_ROUTES, type EstatesToolbarUrl } from '../constants/routes';
import { EstateFilterDrawer } from './estate-filter-drawer';

type EstateFilters = {
  searchString?: string;
  propertyType?: 'house' | 'apartment';
  marketType?: 'buy' | 'rent' | 'sell';
  usageType?: 'commercial';
  propertyLevel?: string;
};

export interface ToolbarProps {
  url: EstatesToolbarUrl;
  showViewSwitch?: boolean;
  creatorList?: SelectOption[];
  rightButtonAction?: React.ReactNode;
  searchString?: string;
  onSearchStringChange?: (value: string) => void;
}

export const Toolbar = ({
  url,
  creatorList,
  showViewSwitch = true,
  rightButtonAction,
  onSearchStringChange,
}: ToolbarProps) => {
  const { t } = useTranslation();
  const route = getRouteApi(url);

  const search = route.useSearch();
  const location = route.useMatch();
  const navigate = route.useNavigate();

  const isMainEstatesPage = url === ESTATES_TOOLBAR_ROUTES.ESTATES;

  const filters = useMemo<EstateFilters>(() => {
    return (search.filters ?? {}) as EstateFilters;
  }, [search.filters]);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const [inputValue, setInputValue] = useState(() => {
    const value = filters.searchString;
    return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
  });

  useEffect(() => {
    if (!isMainEstatesPage) return;

    const input = inputRef.current;
    if (!input) return;

    if (document.activeElement !== input) {
      input.focus();
      const length = input.value.length;
      input.setSelectionRange?.(length, length);
    }
  }, [isMainEstatesPage]);

  const updateFilters = useCallback(
    (updater: (filters: EstateFilters, prev: typeof search) => EstateFilters) => {
      navigate({
        search: (prev) => {
          const prevFilters = (prev.filters ?? {}) as EstateFilters;
          const nextFilters = updater({ ...prevFilters }, prev);

          const prevKeys = Object.keys(prevFilters);
          const nextKeys = Object.keys(nextFilters);

          const hasSameLength = prevKeys.length === nextKeys.length;
          const hasSameValues =
            hasSameLength &&
            prevKeys.every(
              (key) =>
                prevFilters[key as keyof EstateFilters] === nextFilters[key as keyof EstateFilters]
            );

          if (hasSameValues) {
            return prev;
          }

          return {
            ...prev,
            filters: nextFilters,
          };
        },
      });
    },
    [navigate]
  );

  const debouncedUpdateSearch = useDebouncedCallback((value: string) => {
    if (onSearchStringChange) {
      onSearchStringChange(value);
    } else {
      updateFilters((filters) => {
        if (value.trim().length) {
          filters.searchString = value;
        } else {
          delete filters.searchString;
        }
        return filters;
      });
    }
  }, 1000);
  const handleClearSearch = useCallback(() => {
    setInputValue('');
    if (onSearchStringChange) {
      onSearchStringChange('');
    } else {
      updateFilters((filters) => {
        delete filters.searchString;
        return filters;
      });
    }
    const input = inputRef.current;
    if (input) {
      input.focus();
    }
  }, [onSearchStringChange, updateFilters]);

  if (!isMainEstatesPage) {
    return (
      <>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <FilterButton url={url} />
            {showViewSwitch && <ViewSwitch url={url} from={location.pathname} />}
          </div>
          {rightButtonAction}
        </div>
        <EstateFilterDrawer url={url} creatorList={creatorList} />
      </>
    );
  }

  return (
    <>
      <div className="new-ui flex flex-col gap-6">
        <div className="flex flex-wrap gap-3">
          <div className="relative min-w-[240px] flex-1">
            <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <NewUiInput
              ref={inputRef}
              autoFocus
              className={cn('pl-9', inputValue && 'pr-9')}
              placeholder={t('search-estates-placeholder')}
              value={inputValue}
              onChange={(e) => {
                const value = e.target.value;
                setInputValue(value);
                debouncedUpdateSearch(value);
              }}
            />
            {inputValue ? (
              <button
                type="button"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  handleClearSearch();
                }}
                aria-label={t('clearSearch')}
                className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
              >
                <XIcon className="h-4 w-4" />
              </button>
            ) : null}
          </div>
          <div className="flex gap-3">
            <EstatesFilterTrigger />
            {showViewSwitch ? <EstatesViewMenu /> : null}
            <EstatesNewEstateButton />
          </div>
        </div>

        <EstatesQuickFilters />
      </div>

      <EstateFilterDrawer url={url} creatorList={creatorList} />
    </>
  );
};
